import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import Webcam from 'react-webcam';

import { Button, ButtonVariant } from '@/components/form/button';
import { useSize } from '@/hooks';

import { CaptureIcon } from '../capture-icon';

interface IProps {
  onCapture: (data: string | null) => void;

  /** disable capture */
  disableCapture?: boolean;

  /** number of images can be taken */
  limit?: number;

  /** current count of images */
  count?: number;

  /** capture button ref, if provided button will be hidden */
  captureBtnRef?: React.RefObject<HTMLButtonElement>;

  /** broadcast selected camera resolution and aspect ratio */
  onCameraMount?: (settings: {
    width: number;
    height: number;
    aspectRatio: number;
  }) => void;
}

/**
 * width: 9999 = This constraint will return the highest possible resolution supported by each device.
 *
 * Read more about the vide constraints here:
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#parameters
 *
 * The bloody fox does not support the video constraints as chrome does. -_-
 *
 */
const baseVideoConstraints = isFirefox ? { height: 1080 } : { width: 9999 };

export const Camera: React.FC<IProps> = ({
  limit,
  count,
  onCapture,
  onCameraMount,
  captureBtnRef,
  disableCapture = false,
}) => {
  const [deviceId, setDeviceId] = useState<string | undefined>('');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [key, setKey] = useState<number>(0); // Add a key to force remount of Webcam

  const selectRef = useRef<HTMLSelectElement>(null);

  const webcamRef = useRef<Webcam & HTMLVideoElement>(null);
  const webcamContainerRef = useRef<HTMLDivElement>(null);
  const size = useSize(webcamContainerRef);

  /**
   * Since we rotate the camera by 90 deg, we have to re-position the webcam container correctly.
   *
   * Also we are cutting 10% from each side of the camera view to make it looks nice. Otherwise we
   * will have a too wide preview.
   */
  const webcamContainerHeight = useMemo(() => {
    return size?.width ? size?.width * 0.8 : 100;
  }, [size]);
  const webcamContainerTopMargin = useMemo(() => {
    const height = size?.height ? size?.height : 100;
    return (webcamContainerHeight - height) / 2;
  }, [size]);

  // Create a memoized videoConstraints object that includes the deviceId
  const videoConstraints = useMemo(() => {
    return {
      ...baseVideoConstraints,
      deviceId: deviceId ? { exact: deviceId } : undefined,
    };
  }, [deviceId]);

  // this method will return a base64 encoded string of the image
  const capture = useCallback(
    debounce(
      () => {
        if (webcamRef.current) {
          const imageSrc = webcamRef.current.getScreenshot();

          // if user is typing, don't capture images.
          // this is to prevent images being captured when user is typing and press spacebar
          // checkout the event listener in this file
          if (document.activeElement?.localName !== 'input') onCapture(imageSrc);
        }
      },
      2000,
      { leading: true, trailing: false },
    ),
    [webcamRef],
  );

  const updateCameraSettings = () => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoDevices = uniqBy(
          devices.filter(({ kind }) => kind === 'videoinput'),
          'deviceId',
        );
        setDevices(videoDevices);

        // Only set deviceId if it's not already set
        if (!deviceId && videoDevices.length > 0) {
          const device = videoDevices[0];
          setDeviceId(device.deviceId);
        }
      })
      .catch((err) => {
        console.error(`${err.name}: ${err.message}`);
      });
  };

  /**
   * browser will ask for the permissions upon component mount.
   * once the permission granted, user can select the right camera device.
   */
  useEffect(() => {
    updateCameraSettings();
  }, []);

  // add keydown event listener only when the camera is mounted
  useEffect(() => {
    const triggerCapture = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !disableCapture) capture();
    };
    document.removeEventListener('keydown', triggerCapture, false);
    document.addEventListener('keydown', triggerCapture, false);
    return () => {
      document.removeEventListener('keydown', triggerCapture, false);
    };
  }, [deviceId, disableCapture]);

  const onCameraChange = (stream: MediaStream) => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = uniqBy(
        devices.filter(({ kind }) => kind === 'videoinput'),
        'deviceId',
      );
      setDevices(videoDevices);
    });

    const tracks = stream.getVideoTracks();

    if (tracks.length > 0) {
      setDeviceId(tracks[0]?.getSettings().deviceId);
      const { width, height, aspectRatio = 1.777 } = tracks[0].getSettings();
      if (width && height && aspectRatio && onCameraMount)
        onCameraMount({ width, height, aspectRatio });
    }
  };

  const handleDeviceChange = (newDeviceId: string) => {
    setDeviceId(newDeviceId);
    // Force remount of Webcam component to apply new deviceId
    setKey((prevKey) => prevKey + 1);
  };

  const preventEvent = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    if (selectRef.current) selectRef.current.blur();
  };

  const renderCameraLabel = (label: string, deviceId: string) => {
    if (label) return label;
    return `Camera : ${String(deviceId).substring(0, 10).toUpperCase()}`;
  };

  return (
    <div className="flex flex-col gap-2">
      {devices.length > 0 && (
        <select
          className="block relative focus:ring-0 focus:border-gray-200 rounded-md bg-gray border-gray-100 w-full pl-6"
          value={deviceId}
          onChange={(e) => handleDeviceChange(e.target.value)}
          onKeyDown={preventEvent}
          ref={selectRef}
        >
          {devices.map(({ label, deviceId }) => (
            <option key={deviceId} value={deviceId}>
              {renderCameraLabel(label, deviceId!)}
            </option>
          ))}
        </select>
      )}
      <div
        className="relative inline-block overflow-hidden rounded-md bg-gray"
        style={{ height: webcamContainerHeight }}
      >
        <div
          className="flex rotate-90"
          style={{ marginTop: webcamContainerTopMargin }}
          ref={webcamContainerRef}
        >
          <Webcam
            key={key} // Add key to force remount when camera changes
            audio={false}
            ref={webcamRef}
            screenshotQuality={1}
            imageSmoothing={false}
            screenshotFormat="image/jpeg"
            forceScreenshotSourceSize={true} // this is important https://github.com/mozmorris/react-webcam#props
            videoConstraints={videoConstraints}
            onUserMedia={onCameraChange}
            style={{
              borderRadius: '8px',
            }}
          />
        </div>

        <div className="absolute flex flex-col items-center w-full gap-1 bottom-2">
          {limit ? (
            <span className="bg-gray-400 text-xs font-light py-[0.125rem] px-1 rounded-3xl text-white">
              {count} / {limit}
            </span>
          ) : null}
          {!disableCapture ? (
            <div>
              {captureBtnRef ? (
                <button className="hidden" onClick={capture} ref={captureBtnRef}>
                  Capture
                </button>
              ) : (
                <div className="has-tooltip">
                  <Button
                    classnames="rounded-full transition duration-300 ease-in-out opacity-60 hover:opacity-100"
                    variant={ButtonVariant.Link}
                    onClick={capture}
                  >
                    <CaptureIcon />
                  </Button>
                  <span className="p-1 mt-3 ml-3 text-xs text-white bg-gray-600 rounded shadow-lg tooltip">
                    Click or press &apos;Space&apos;
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
